// Musubi theme service — light (default) / dark ("ink") toggle.
//
// The active theme is a single `data-theme` attribute on <html>, read by the
// token layer (icarus/src/scss/_tokens.scss). Persistence is per-device via a
// dedicated localStorage key so the choice survives reloads and is applied
// BEFORE first paint (see index.js) to avoid a flash-of-wrong-theme.
//
// ⚠ Storage key is `musubi-theme` — deliberately NOT `tokens` (the pinia
// persisted-state key), which must never be touched.
//
// F-OB-03 — cross-device follow. `GET/PUT /api/v1/shell/me/preferences`
// exposes a `{ theme }` user_meta field (backend: worker D). localStorage
// stays the pre-paint cache (index.js reflects it synchronously, before any
// network round trip could resolve, so there is never a boot-time FOUC); the
// server value is an ASYNC upgrade layered on top:
//   - seedThemeFromServer() — called once at boot (index.js), after Pinia is
//     live so the auth token is available. Fire-and-forget from the caller's
//     POV: never awaited before mount, never throws, silently no-ops on a
//     404 (endpoint not deployed yet), 401/403 (logged out), or network
//     error. If the server disagrees with the local cache (e.g. first load
//     on a new device) it re-paints to match — a rare, acceptable one-time
//     flash, not the common case.
//   - applyTheme() — the single write path (toggle button + any future
//     settings UI should call this, not touch localStorage/data-theme
//     directly) mirrors every user-initiated change to the server via a
//     fire-and-forget PUT. seedThemeFromServer() intentionally does NOT
//     round-trip back through applyTheme()'s PUT — it uses the same local
//     persist step without re-PUTting the value the server just supplied.
//
// The preference endpoint may not exist yet at any given moment (it's a
// parallel-workstream build) — every network call here is wrapped so a
// missing/erroring endpoint degrades silently to "localStorage only", which
// is exactly today's behaviour.

/* global BACKEND_DOMAIN */
import useTokenStore from '@apexl/icarus-core/src/store/useTokenStore';

export const THEME_STORAGE_KEY = 'musubi-theme';
export const THEMES = ['light', 'dark'];
const DEFAULT_THEME = 'light';
const PREFERENCES_ENDPOINT = '/api/v1/shell/me/preferences';

/** Read the persisted theme, falling back to the light default. */
export function getStoredTheme() {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(v) ? v : DEFAULT_THEME;
  } catch (_) {
    return DEFAULT_THEME;
  }
}

/** Reflect a theme onto <html data-theme> without persisting. */
export function reflectTheme(theme) {
  const t = THEMES.includes(theme) ? theme : DEFAULT_THEME;
  document.documentElement.setAttribute('data-theme', t);
  return t;
}

/** Reflect + cache locally. Does NOT talk to the server (used by both the
 * public write path below and the boot-time server seed, which must not
 * immediately PUT back the value it just GET-ed). */
function persistLocally(theme) {
  const t = reflectTheme(theme);
  try { window.localStorage.setItem(THEME_STORAGE_KEY, t); } catch (_) { /* private mode */ }
  return t;
}

/** Best-effort bearer token for the preference endpoint. Pinia must already
 * be active (index.js sets this up before calling anything here that needs
 * it) — defensive try/catch in case this is ever called earlier. */
function authHeader() {
  try {
    const token = useTokenStore().accessToken;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

/** Fire-and-forget PUT of the current theme to the server. Never throws,
 * never awaited by callers — a failure here just means the device-local
 * cache is the only copy for now, same as before this feature existed. */
function syncThemeToServer(theme) {
  try {
    fetch(BACKEND_DOMAIN + PREFERENCES_ENDPOINT, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeader() },
      body: JSON.stringify({ theme }),
    }).catch(() => { /* offline / endpoint missing — non-fatal */ });
  } catch (_) { /* no-op */ }
}

/** Persist + apply a theme, mirroring the write server-side. The single
 * write path — the rail's theme toggle calls this (not localStorage
 * directly) so every user-initiated change follows the same rules. */
export function applyTheme(theme) {
  const t = persistLocally(theme);
  syncThemeToServer(t);
  return t;
}

/** Flip between light and dark, persisting + applying the result. */
export function toggleTheme() {
  const next = getStoredTheme() === 'dark' ? 'light' : 'dark';
  return applyTheme(next);
}

/**
 * Boot-time seed (F-OB-03). Call once, after Pinia is installed, before
 * `app.mount()`. Deliberately not awaited by index.js — the synchronous
 * `reflectTheme(getStoredTheme())` call already painted the correct-for-
 * this-device theme with zero network dependency; this only upgrades it if
 * the server has a different preference on record.
 */
export async function seedThemeFromServer() {
  try {
    const res = await fetch(BACKEND_DOMAIN + PREFERENCES_ENDPOINT, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', ...authHeader() },
    });
    if (!res || !res.ok) return; // 404 (not shipped yet) / 401 / 403 — local cache stands
    const body = await res.json().catch(() => null);
    const theme = body?.theme ?? body?.data?.theme ?? null;
    if (THEMES.includes(theme) && theme !== getStoredTheme()) {
      persistLocally(theme);
    }
  } catch (_) {
    // Network error / offline — non-fatal, localStorage cache stands.
  }
}
